import { useState } from "preact/hooks"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { api_fetch_throws, updateCheck, CheckDetail } from "@/api"
import { toast } from "@/components/ui/use-toast.ts"
import { check_api_path } from "./check_path"

/**
 * How a check came to be applied: `remote` = a payment or credit memo exists in the accounting
 * system (can't be undone here), `manual` = a user marked it applied (brands with no accounting
 * integration apply cash elsewhere), `not_applied` = neither.
 */
export type CheckAppliedState = "remote" | "manual" | "not_applied"

export interface CheckAppliedFields {
  applied_payment_remote_id?: string | null
  applied_credit_memo_remote_id?: string | null
  applied_at?: string | null
}

export function check_applied_state(check: CheckAppliedFields): CheckAppliedState {
  if (check.applied_payment_remote_id || check.applied_credit_memo_remote_id) {
    return "remote"
  }
  return check.applied_at ? "manual" : "not_applied"
}

/** The `check` row is created lazily by the detail endpoint; list rows may not have one yet. */
async function resolve_check_id(check_id: string | undefined, check_number: string): Promise<string | undefined> {
  if (check_id) return check_id
  const detail = await api_fetch_throws<CheckDetail>(check_api_path(check_number))
  return detail.id
}

interface ManualAppliedToggleProps {
  check_id?: string
  check_number: string
  /** Current state; the toggle renders nothing for a remotely applied check. */
  state: CheckAppliedState
  on_change: (updated: CheckDetail) => void
  size?: "default" | "sm"
  className?: string
}

/** "Mark as applied" / "Mark as not applied" for checks whose cash is applied outside Promoted. */
export function ManualAppliedToggle({ check_id, check_number, state, on_change, size, className }: ManualAppliedToggleProps) {
  const [saving, set_saving] = useState(false)

  if (state === "remote") return null

  const next_applied = state === "not_applied"

  async function handle_click(event: MouseEvent) {
    // The list renders this inside a clickable row.
    event.stopPropagation()
    set_saving(true)
    try {
      const id = await resolve_check_id(check_id, check_number)
      const updated = await updateCheck(id, { applied: next_applied })
      on_change(updated)
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Could not update check",
        description: (error as { message?: string })?.message || "Please try again.",
      })
    } finally {
      set_saving(false)
    }
  }

  return (
    <Button
      variant="outline"
      size={size}
      class={className}
      disabled={saving}
      onClick={handle_click}
      title={next_applied ? "Record that this check's cash has been applied" : "Undo the manual applied mark"}>
      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
      {next_applied ? "Mark as applied" : "Mark as not applied"}
    </Button>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("check_applied_state", () => {
    it("is remote when a payment exists", () => {
      expect(check_applied_state({ applied_payment_remote_id: "123", applied_at: "2026-09-01T00:00:00Z" })).toBe("remote")
    })

    it("is remote when only a credit memo matched", () => {
      expect(check_applied_state({ applied_credit_memo_remote_id: "cm1" })).toBe("remote")
    })

    it("is manual when applied_at is set without remote ids", () => {
      expect(check_applied_state({ applied_at: "2026-09-01T00:00:00Z" })).toBe("manual")
    })

    it("is not applied otherwise", () => {
      expect(check_applied_state({})).toBe("not_applied")
      expect(check_applied_state({ applied_at: null })).toBe("not_applied")
    })
  })
}
